/* eslint-disable @typescript-eslint/naming-convention */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { getBrandList, GetBrandListParamsType } from 'features/Product/services/brand'
import { postCreateProductApi } from 'features/Product/services/product'
import {
  getCategoriesApi,
  getCategoryByIdApi,
  // getCategoryOlds,
  getProductAttributeOptionApi,
  getWimsConstants,
  getWimsProductTypes,
  getWimsProductUoms,
  getWimsStorageHandlings,
  type GetCategoryQueryParamsType,
} from 'features/Product/services/productAdd'
import { getParsedErrorResponse } from 'helpers/errorMsg'
import { GetDiscoveryLabelReqParamsType } from 'redux/productLabel'
import { getDiscoveryLabel } from 'utils/apiList/dicoveryLabel'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import {
  OperationalConstantsType,
  ProductAddEditPayloadType,
  ProductAttributeTypeType,
  ProductConstantType,
} from 'features/Product/@types/ProductAdd'
import { postImage } from 'utils/apiList/images'
import { getAssetMasterListAPI } from 'features/AssetManagement/services/assetMaster'

export const SLICE_NAME_PRODUCT_ADD = 'productAdd'

export const fetchProductCategories = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchProductCategories`,
  async (params: GetCategoryQueryParamsType, { rejectWithValue }) => {
    try {
      const response = await getCategoriesApi(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchProductTypes = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchProductTypes`,
  async () => {
    try {
      const response = await getWimsProductTypes()
      return response.data.data || []
    } catch {
      return []
    }
  },
)

export const fetchAttributeOptions = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchAttributeOptions`,
  async (type: ProductAttributeTypeType) => {
    try {
      const response = await getProductAttributeOptionApi({ type })
      return response.data.data || []
    } catch {
      return []
    }
  },
)

export const fetchBrands = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchBrands`,
  async (params: GetBrandListParamsType) => {
    try {
      const response = await getBrandList(params)
      return response.data.data || []
    } catch {
      return []
    }
  },
)

export const fetchDiscoveryLabel = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchDiscoveryLabel`,
  async (params: GetDiscoveryLabelReqParamsType) => {
    try {
      const response = await getDiscoveryLabel(params)
      return response.data.content || []
    } catch {
      return []
    }
  },
)

export const fetchGetWimsConstants = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchConstants`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getWimsConstants()
      return response.data.data
    } catch {
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetStorageHandling = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchStorageHandling`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getWimsStorageHandlings({
        type: 'ENVIRONMENT',
      })
      return response.data.data || []
    } catch {
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetAssetsMaster = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchAssetsMaster`,
  async (
    params:
      | {
          key: 'storageBinTypeHub' | 'storageBinTypeWarehouse' | 'storageOutboundTypeHub'
          search?: string
        }
      | undefined,
    { rejectWithValue },
  ) => {
    const { search } = params || {}
    try {
      const response = await getAssetMasterListAPI({
        type: 'moveable',
        page_size: 100,
        name: search,
      })
      return (
        response.data.data.map((item) => ({
          id: item.id,
          name: item.name,
        })) || []
      )
    } catch {
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetBaseUom = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchBaseUom`,
  async (search: string | undefined, { rejectWithValue }) => {
    try {
      const response = await getWimsProductUoms({
        'filter.groups': 'ITEM',
        'filter.active': true,
        'pagination.page_size': 100,
        'pagination.page_index': 1,
        'filter.id': search || '',
      })

      return (
        response.data.data.map((item) => ({
          code: item.id,
          name: `${item.description} (${item.id})`,
        })) || []
      )
    } catch {
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchOperationalConstants = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchOperationalConstants`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const [wimsConstants, storageHandlings, baseUoms, assetsMaster] = await Promise.all([
        dispatch(fetchGetWimsConstants()).unwrap(),
        dispatch(fetchGetStorageHandling()).unwrap(),
        dispatch(fetchGetBaseUom()).unwrap(),
        dispatch(fetchGetAssetsMaster()).unwrap(),
      ])

      return {
        storageHandlings,
        baseUoms,
        shelfs: wimsConstants.product_shelves,
        specialHandlingDetails: wimsConstants.product_special_handling,
        storageTypes: wimsConstants.product_storage_types,
        // pickingUoms: wimsConstants.picking_uoms,
        // receivingUoms: wimsConstants.receiving_uoms,
        // vendorUoms: wimsConstants.vendor_uoms,
        productPlacements: wimsConstants.product_placements,
        skuMovementHandlings: wimsConstants.product_movement_handling,
        imageHost: wimsConstants.product_image_host,
        bomCategories: wimsConstants.product_bill_of_material_criteria,
        socialMediaList: wimsConstants.social_media_list,
        ppnList: wimsConstants.product_ppn,
        papLocationTypes: wimsConstants.pap_location_types,
        registrationStatus: wimsConstants.product_registration_statuses,
        sourceStatus: wimsConstants.product_source_status,
        uomTypes: wimsConstants.product_type_uom,
        storageBinTypeHub: assetsMaster,
        storageBinTypeWarehouse: assetsMaster,
        storageOutboundTypeHub: assetsMaster,
      } as OperationalConstantsType
    } catch (e) {
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const uploadProductImage = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/uploadProductImage`,
  async (file: File, { getState, rejectWithValue }) => {
    const {
      auth: { userData },
    } = getState() as StoreStateType

    try {
      const {
        data: { data },
      } = await postImage('product-images', {
        file_input: file,
        created_by: userData.id,
      })
      return data
    } catch (error) {
      let message = 'Terjadi kesalahan saat upload gambar produk.'

      if (error instanceof AxiosError && error.response) {
        message = getParsedErrorResponse(error.response)
      }

      return rejectWithValue(message)
    }
  },
)

export const createProduct = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/createProduct`,
  async (payload: ProductAddEditPayloadType, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await postCreateProductApi(payload)
      return data
    } catch (error) {
      let message = 'Terjadi kesalahan saat create produk.'

      if (error instanceof AxiosError && error.response) {
        message = getParsedErrorResponse(error.response)
      }

      return rejectWithValue(message)
    }
  },
)

export const fetchGetCategoryById = createAsyncThunk(
  `${SLICE_NAME_PRODUCT_ADD}/fetchGetCategoryById`,
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getCategoryByIdApi(id)
      return response.data
    } catch {
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const setConstants = createAction<Partial<ProductConstantType>>(
  `${SLICE_NAME_PRODUCT_ADD}/setConstants`,
)
